import React, { useState, useEffect } from 'react';
import { 
  Newspaper, Calendar, ArrowRight, Search, ChevronDown,
  Loader2, Mail, Bell, CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Todos', 'Tecnología', 'Proyectos', 'Empresa', 'Industria', 'Eventos', 'Tips'];

const NoticiasPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNews();
  }, [selectedCategory]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'Todos' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/news/public${params}`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    // Simulate subscription
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubscribed(true);
    setSubscribing(false);
  };

  // Filter by search
  const filteredNews = news.filter(n => 
    n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const featuredNews = filteredNews.filter(n => n.is_featured);
  const regularNews = filteredNews.filter(n => !n.is_featured);

  return (
    <div className="min-h-screen bg-white" data-testid="noticias-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Newspaper className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Noticias</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Actualidad Zenex
            <span className="block text-emerald-400">y el Mundo Solar</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl">
            Mantenete informado sobre las últimas novedades en energía solar, 
            proyectos destacados y avances tecnológicos.
          </p>
        </div>
      </section>

      {/* Search and Categories */}
      <section className="py-8 bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar noticias..."
                className="w-full pl-12 pr-4 py-3 border border-zinc-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-2">
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="text-center py-20">
              <Newspaper className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">No hay noticias disponibles</h3>
              <p className="text-zinc-500">
                {searchTerm 
                  ? 'No encontramos noticias que coincidan con tu búsqueda' 
                  : 'Pronto publicaremos nuevas noticias. ¡Volvé a visitarnos!'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Featured News */}
              {featuredNews.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-zinc-900 mb-6">Destacadas</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {featuredNews.map((item) => (
                      <Link 
                        key={item.id}
                        to={`/noticias/${item.slug}`}
                        className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                      >
                        {item.image_url && (
                          <div className="h-64 overflow-hidden">
                            <img 
                              src={item.image_url} 
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full mb-3">
                            {item.category}
                          </span>
                          <h3 className="text-xl font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-zinc-600 mb-4 line-clamp-2">{item.excerpt}</p>
                          <div className="flex items-center justify-between text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(item.publish_date || item.created_at)}
                            </span>
                            <span className="text-emerald-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                              Leer más <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular News Grid */}
              {regularNews.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900 mb-6">
                    {featuredNews.length > 0 ? 'Más Noticias' : 'Todas las Noticias'}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {regularNews.map((item) => (
                      <Link 
                        key={item.id}
                        to={`/noticias/${item.slug}`}
                        className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-zinc-100"
                      >
                        {item.image_url && (
                          <div className="h-48 overflow-hidden">
                            <img 
                              src={item.image_url} 
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-5">
                          <span className="inline-block px-2 py-0.5 bg-zinc-100 text-zinc-600 text-xs font-medium rounded mb-2">
                            {item.category}
                          </span>
                          <h3 className="font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-zinc-500 mb-3 line-clamp-2">{item.excerpt}</p>
                          <div className="flex items-center text-xs text-zinc-400">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(item.publish_date || item.created_at)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Bell className="w-12 h-12 text-white/80 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Suscribite a nuestro Newsletter
          </h2>
          <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
            Recibí las últimas noticias sobre energía solar, tips de ahorro 
            y promociones exclusivas directamente en tu correo.
          </p>
          
          {subscribed ? (
            <div className="flex items-center justify-center gap-2 text-white">
              <CheckCircle className="w-6 h-6" />
              <span className="text-lg font-medium">¡Gracias por suscribirte!</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  required
                  className="w-full pl-12 pr-4 py-4 rounded-xl focus:ring-2 focus:ring-white/50 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={subscribing}
                className="px-8 py-4 bg-zinc-900 text-white font-semibold rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {subscribing && <Loader2 className="w-5 h-5 animate-spin" />}
                Suscribirse
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default NoticiasPage;
